import MemberPage from './components/MemberPage';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import ManagerDashboard from './components/ManagerDashboard';
import LoadingSpinner from './components/LoadingSpinner';
import SiteSelector from './components/SiteSelector';
import SystemLanding from './components/SystemLanding';
import TrainingTree from './components/TrainingTree';
import Administration from './components/Administration';
import LocationList from './components/LocationList';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import useNavigationLogger from './hooks/useNavigationLogger';
import { useSiteNavigation } from './hooks/useSiteNavigation';
import { buildApiUrl, API_ENDPOINTS, authenticatedFetch } from './config/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubdomainProvider, useSubdomain } from './contexts/SiteContext';
import './index.css';
import './components/SitePages.css';
import './components/SystemLanding.css';

function Home() {
  const { loading: authLoading } = useAuth();

  // Mostrar loading solo si la autenticación está cargando
  if (authLoading) {
    return <LoadingSpinner message="Cargando..." />;
  }

  // Mostrar el SystemLanding para todos los usuarios
  // Este componente maneja internamente la lógica según el tipo de usuario
  // y carga los datos necesarios (subdomains, etc.) por sí mismo
  return <SystemLanding />;
}

function Members() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [showAddForm, setShowAddForm] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const { siteId } = useParams();
  const { activeSite } = useSubdomain();

  useEffect(() => {
    async function fetchMembers() {
      try {
        // Los miembros ahora siempre requieren un siteId
        if (!siteId) {
          setError('No se ha especificado un centro de entrenamiento');
          setLoading(false);
          return;
        }
        
        const apiUrl = buildApiUrl(API_ENDPOINTS.SITE_MEMBERS(siteId));
        const response = await authenticatedFetch(apiUrl);
        if (!response.ok) {
          throw new Error('Error al obtener los miembros');
        }
        const data = await response.json();
        setMembers(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchMembers();
  }, [siteId]);

  const handleAddMember = () => {
    // setShowAddForm(true);
    console.log('Add member functionality not implemented yet');
  };

  const handleViewTrainingTree = (memberId) => {
    setSelectedMemberId(memberId);
  };

  const handleCloseTrainingTree = () => {
    setSelectedMemberId(null);
  };

  if (loading) return <LoadingSpinner message="Cargando miembros..." />;
  if (error) return <div className="error-message">Error: {error}</div>;
  if (!siteId) return <div className="error-message">Error: No se ha especificado un centro de entrenamiento</div>;

  return (
    <div className="card">
      <div className="members-header">
        <h2>
          {activeSite ? `Miembros de ${activeSite.name}` : 'Miembros'}
        </h2>
        <button 
          onClick={handleAddMember}
          className="btn btn-primary"
        >
          ➕ Agregar Miembro
        </button>
      </div>
      
      {!members || members.length === 0 ? (
        <div className="no-members">
          <div className="no-members-icon">👥</div>
          <h3>No hay miembros registrados</h3>
          <p>Comienza agregando el primer miembro a este centro de entrenamiento.</p>
          <button 
            onClick={handleAddMember}
            className="btn btn-primary btn-lg"
          >
            Agregar Primer Miembro
          </button>
        </div>
      ) : (
        <div className="members-list">
          {members && members.map(member => (
            <div key={member.id} className="member-item">
              <div className="member-info">
                <h3>{member.name}</h3>
                <p>Email: {member.email}</p>
                <p>Rol: {member.role}</p>
              </div>
              <div className="member-actions">
                <button 
                  onClick={() => window.location.href = `/site/${siteId}/member/${member.id}`}
                  className="btn btn-secondary btn-sm"
                >
                  Ver Detalles
                </button>
                <button 
                  onClick={() => handleViewTrainingTree(member.id)}
                  className="btn btn-info btn-sm"
                >
                  🌳 Árbol de Entrenamiento
                </button>
                <button className="btn btn-warning btn-sm">
                  Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Training Tree Modal/Overlay */}
      {selectedMemberId && (
        <div className="training-tree-overlay">
          <div className="training-tree-container">
            <div className="training-tree-header">
              <h3>Árbol de Entrenamiento</h3>
              <button 
                onClick={handleCloseTrainingTree}
                className="btn btn-secondary btn-sm"
              >
                ✕ Cerrar
              </button>
            </div>
            <div className="training-tree-content">
              <TrainingTree memberId={selectedMemberId} siteId={siteId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Locations() {
  return <div>Gestiona y visualiza las ubicaciones de práctica aquí.</div>;
}


// Componente para el dashboard de un site específico
function SiteDashboard() {
  const { currentSite, isInSiteContext, hasAccess } = useSiteNavigation();
  const { locations, loadLocationsBySite } = useSubdomain();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentSite && isInSiteContext) {
      loadLocationsBySite(currentSite.id);
    }
  }, [currentSite, isInSiteContext, loadLocationsBySite]);

  if (!hasAccess) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>🚫 Acceso Denegado</h2>
          <p className="access-denied-message">
            No tienes acceso a este centro de entrenamiento.
          </p>
          <p className="access-denied-reason">
            El subdominio de la URL no coincide con el subdominio configurado para este centro.
          </p>
          <div className="access-denied-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver al Inicio
            </button>
            <button 
              onClick={() => navigate('/manager')}
              className="btn-secondary"
            >
              Ir a Mi Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentSite) {
    return <LoadingSpinner message="Cargando centro de entrenamiento..." />;
  }

  return (
    <div className="site-dashboard">
      <div className="site-header">
        <h1>{currentSite.name}</h1>
        <p className="site-description">{currentSite.description}</p>
        <SiteSelector compact={true} />
      </div>
      
      <div className="site-stats">
        <div className="stat-card">
          <h3>Ubicaciones</h3>
          <p className="stat-number">{locations.length}</p>
        </div>
        <div className="stat-card">
          <h3>Deportes</h3>
          <p className="stat-number">{currentSite.sports?.length || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Miembros</h3>
          <p className="stat-number">0</p>
        </div>
      </div>
    </div>
  );
}

// Componente para la gestión de ubicaciones de un site
function SiteLocations() {
  const { currentSite, hasAccess } = useSiteNavigation();
  const navigate = useNavigate();

  if (!hasAccess) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>🚫 Acceso Denegado</h2>
          <p className="access-denied-message">
            No tienes acceso a este centro de entrenamiento.
          </p>
          <p className="access-denied-reason">
            El subdominio de la URL no coincide con el subdominio configurado para este centro.
          </p>
          <div className="access-denied-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver al Inicio
            </button>
            <button 
              onClick={() => navigate('/manager')}
              className="btn-secondary"
            >
              Ir a Mi Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentSite) {
    return <LoadingSpinner message="Cargando centro de entrenamiento..." />;
  }

  const handleLocationSelect = (location) => {
    // Navegar a la vista de horarios de la ubicación específica
    navigate(`/site/${currentSite.id}/locations/${location.id}/schedules`);
  };

  return (
    <div className="site-locations">
      <div className="page-header">
        <h1>Ubicaciones - {currentSite.name}</h1>
        <SiteSelector compact={true} />
      </div>
      
      <div className="locations-content">
        <LocationList
          siteId={currentSite.id}
          siteName={currentSite.name}
          onLocationSelect={handleLocationSelect}
          showCreateButton={true}
        />
      </div>
    </div>
  );
}

// Componente para la gestión de horarios de un site
function SiteSchedules() {
  const { currentSite, hasAccess } = useSiteNavigation();
  const navigate = useNavigate();

  if (!hasAccess) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>🚫 Acceso Denegado</h2>
          <p className="access-denied-message">
            No tienes acceso a este centro de entrenamiento.
          </p>
          <p className="access-denied-reason">
            El subdominio de la URL no coincide con el subdominio configurado para este centro.
          </p>
          <div className="access-denied-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver al Inicio
            </button>
            <button 
              onClick={() => navigate('/manager')}
              className="btn-secondary"
            >
              Ir a Mi Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentSite) {
    return <LoadingSpinner message="Cargando centro de entrenamiento..." />;
  }

  return (
    <div className="site-schedules">
      <div className="page-header">
        <h1>Horarios - {currentSite.name}</h1>
        <SiteSelector compact={true} />
      </div>
      
      <div className="schedules-content">
        <p>Gestiona los horarios de práctica de tu centro de entrenamiento.</p>
        <LocationList
          siteId={currentSite.id}
          siteName={currentSite.name}
          showCreateButton={true}
          compact={true}
        />
      </div>
    </div>
  );
}

// Componente para la configuración de un site
function SiteSettings() {
  const { currentSite, hasAccess } = useSiteNavigation();
  const navigate = useNavigate();

  if (!hasAccess) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>🚫 Acceso Denegado</h2>
          <p className="access-denied-message">
            No tienes acceso a este centro de entrenamiento.
          </p>
          <p className="access-denied-reason">
            El subdominio de la URL no coincide con el subdominio configurado para este centro.
          </p>
          <div className="access-denied-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver al Inicio
            </button>
            <button 
              onClick={() => navigate('/manager')}
              className="btn-secondary"
            >
              Ir a Mi Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentSite) {
    return <LoadingSpinner message="Cargando centro de entrenamiento..." />;
  }

  return (
    <div className="site-settings">
      <div className="page-header">
        <h1>Configuración - {currentSite.name}</h1>
        <SiteSelector compact={true} />
      </div>
      
      <div className="settings-content">
        <p>Configura los detalles de tu centro de entrenamiento.</p>
        {/* Aquí se implementará la configuración del site */}
      </div>
    </div>
  );
}

// Componente interno que maneja el logging de navegación
const AppWithLogging = () => {
  // Usar el hook de logging
  useNavigationLogger();

  return (
    <div className="app">
      <Header />
      <main className="main-content">
        <Routes>
          {/* Rutas públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Ruta principal - maneja landing page y contenido de usuario */}
          <Route path="/" element={<Home />} />
          
          {/* Rutas protegidas */}
          <Route path="/site/:siteId/member/:id" element={
            <ProtectedRoute>
              <MemberPage />
            </ProtectedRoute>
          } />
          <Route path="/locations" element={
            <ProtectedRoute>
              <Locations />
            </ProtectedRoute>
          } />
          
          {/* Rutas del manager */}
          <Route path="/manager" element={
            <ProtectedRoute requiredRole="manager">
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/dashboard" element={
            <ProtectedRoute requiredRole="manager">
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/sites" element={
            <ProtectedRoute requiredRole="manager">
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          
          {/* Rutas de administración */}
          <Route path="/administration" element={
            <ProtectedRoute requiredRole="admin">
              <Administration />
            </ProtectedRoute>
          } />
          
          {/* Rutas específicas de sites */}
          <Route path="/site/:siteId" element={
            <ProtectedRoute requiredRole="manager">
              <SiteDashboard />
            </ProtectedRoute>
          } />
          <Route path="/site/:siteId/dashboard" element={
            <ProtectedRoute requiredRole="manager">
              <SiteDashboard />
            </ProtectedRoute>
          } />
          <Route path="/site/:siteId/members" element={
            <ProtectedRoute requiredRole="manager">
              <Members />
            </ProtectedRoute>
          } />
          <Route path="/site/:siteId/locations" element={
            <ProtectedRoute requiredRole="manager">
              <SiteLocations />
            </ProtectedRoute>
          } />
          <Route path="/site/:siteId/schedules" element={
            <ProtectedRoute requiredRole="manager">
              <SiteSchedules />
            </ProtectedRoute>
          } />
          <Route path="/site/:siteId/settings" element={
            <ProtectedRoute requiredRole="manager">
              <SiteSettings />
            </ProtectedRoute>
          } />
          
          {/* Redirección por defecto */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <SubdomainProvider>
        <Router>
          <AppWithLogging />
        </Router>
      </SubdomainProvider>
    </AuthProvider>
  );
};

export default App;
